import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { RestaurantCompletedSaleDto } from '../../../services/restaurantApiService';
import { QuickSaleReceiptDto } from '../../../services/salesService';
import { invoiceService } from '../../../services/invoiceService';
import InvoiceDisplayModal, { InvoiceData } from '../../accounting/invoice/InvoiceDisplayModal';
import { useTranslation } from 'react-i18next';

// ==================== HELPER FUNCTIONS ====================

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount) + ' ₺';
};

const formatDateTime = (dateString: string | Date, addTurkeyOffset = true): string => {
  let date = new Date(dateString);
  // UTC'den gelen tarihlere +3 saat ekle (Türkiye saati)
  if (addTurkeyOffset) {
    date = new Date(date.getTime() + (3 * 60 * 60 * 1000));
  }
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatDuration = (minutes: number, hourLabel: string, minuteLabel: string): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}${hourLabel} ${mins}${minuteLabel}`;
  }
  return `${mins}${minuteLabel}`;
};

// ==================== INTERFACES ====================

export interface SaleDetailModalProps {
  open: boolean;
  onClose: () => void;
  saleDetail: RestaurantCompletedSaleDto | null;
  companyId: string | null;
  // ✅ YENİ: Hızlı satış desteği
  quickSaleDetail?: QuickSaleReceiptDto | null;
}

// ==================== COMPONENT ====================

const SaleDetailModal: React.FC<SaleDetailModalProps> = ({
  open,
  onClose,
  saleDetail,
  companyId,
  quickSaleDetail, // ✅ YENİ: Hızlı satış desteği
}) => {
  const { t } = useTranslation('salesSystem');
  // State
  const [saleItems, setSaleItems] = useState<any[]>([]);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceData | null>(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [accountName, setAccountName] = useState<string | null>(null); // ✅ Cari bilgisi
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState<any[]>([]); // ✅ YENİ: Fatura kalemleri (hızlı satış için)
  const [loadingInvoiceItems, setLoadingInvoiceItems] = useState(false);
  
  // ✅ Hangi satış tipinin gösterileceğini belirle
  const isQuickSale = !!quickSaleDetail;
  const invoiceNumber = isQuickSale ? quickSaleDetail?.InvoiceNumber : saleDetail?.InvoiceNumber;

  // ItemsJson'ı parse et ve cari bilgisini çek
  useEffect(() => {
    // ✅ HIZLI SATIŞ İÇİN
    if (open && quickSaleDetail) {
      console.log('📋 [SaleDetailModal] Hızlı satış detayı:', {
        receiptNumber: quickSaleDetail.ReceiptNumber,
        invoiceNumber: quickSaleDetail.InvoiceNumber,
        totalAmount: quickSaleDetail.TotalAmount,
      });
      
      // Hızlı satış için sipariş kalemleri yok, fatura kalemlerini çek
      setSaleItems([]);
      setAccountName(quickSaleDetail.AccountName || t('endOfDayReport.saleDetail.generalAccount'));
      
      // Fatura kalemlerini çek - InvoiceId varsa doğrudan tekil fatura getir (kalemler dahil)
      if (companyId && (quickSaleDetail.InvoiceId || quickSaleDetail.InvoiceNumber)) {
        setLoadingInvoiceItems(true);
        
        const fetchInvoiceItems = async () => {
          try {
            // Önce InvoiceId ile dene (kalemler dahil gelir)
            if (quickSaleDetail.InvoiceId) {
              console.log('📦 [SaleDetailModal] InvoiceId ile fatura çekiliyor:', quickSaleDetail.InvoiceId);
              const invoice = await invoiceService.getInvoiceById(quickSaleDetail.InvoiceId);
              if (invoice?.items && invoice.items.length > 0) {
                console.log('📦 [SaleDetailModal] Fatura kalemleri bulundu (by ID):', invoice.items.length);
                setInvoiceItems(invoice.items);
                return;
              }
            }
            
            // InvoiceId yoksa veya kalem gelmediyse InvoiceNumber ile fatura numarasından ara
            if (quickSaleDetail.InvoiceNumber) {
              console.log('📦 [SaleDetailModal] InvoiceNumber ile fatura aranıyor:', quickSaleDetail.InvoiceNumber);
              const invoices = await invoiceService.getInvoicesByCompany();
              const invoice = invoices.find(inv => inv.invoiceNumber === quickSaleDetail.InvoiceNumber);
              if (invoice?.id) {
                const fullInvoice = await invoiceService.getInvoiceById(invoice.id);
                if (fullInvoice?.items && fullInvoice.items.length > 0) {
                  console.log('📦 [SaleDetailModal] Fatura kalemleri bulundu (by number->id):', fullInvoice.items.length);
                  setInvoiceItems(fullInvoice.items);
                  return;
                }
              }
            }
            
            console.log('📦 [SaleDetailModal] Fatura kalemleri bulunamadı');
            setInvoiceItems([]);
          } catch (err) {
            console.error('Fatura kalemleri çekilemedi:', err);
            setInvoiceItems([]);
          } finally {
            setLoadingInvoiceItems(false);
          }
        };
        
        fetchInvoiceItems();
      }
      return;
    }
    
    // ✅ RESTORAN İÇİN (eski kod)
    if (open && saleDetail) {
      console.log('📋 [SaleDetailModal] Satış detayı:', {
        saleNumber: saleDetail.SaleNumber,
        invoiceNumber: saleDetail.InvoiceNumber,
        referenceNumber: saleDetail.ReferenceNumber,
        itemsJson: saleDetail.ItemsJson,
      });
      
      // ItemsJson parse
      try {
        const items = saleDetail.ItemsJson ? JSON.parse(saleDetail.ItemsJson) : [];
        console.log('📦 [SaleDetailModal] Parse edilen ürünler:', items);
        setSaleItems(items);
      } catch (e) {
        console.error('ItemsJson parse hatası:', e);
        setSaleItems([]);
      }
      
      // Cari bilgisini faturadan çek - InvoiceNumber ile arama yerine doğrudan ID ile
      if (saleDetail.InvoiceNumber && companyId) {
        setLoadingAccount(true);
        const fetchAccount = async () => {
          try {
            const invoices = await invoiceService.getInvoicesByCompany();
            const invoice = invoices.find(inv => inv.invoiceNumber === saleDetail.InvoiceNumber);
            if (invoice?.accountName) {
              setAccountName(invoice.accountName);
              console.log('👤 [SaleDetailModal] Cari bulundu:', invoice.accountName);
            } else {
              setAccountName(null);
            }
          } catch (err) {
            console.error('Cari bilgisi çekilemedi:', err);
            setAccountName(null);
          } finally {
            setLoadingAccount(false);
          }
        };
        fetchAccount();
      } else {
        setAccountName(null);
      }
    } else if (!quickSaleDetail) {
      setSaleItems([]);
      setInvoiceItems([]);
      setAccountName(null);
    }
  }, [open, saleDetail, quickSaleDetail, companyId]);

  // Faturayı görüntüle
  const handleViewInvoice = async () => {
    const currentInvoiceNumber = isQuickSale ? quickSaleDetail?.InvoiceNumber : saleDetail?.InvoiceNumber;
    const currentInvoiceId = isQuickSale ? quickSaleDetail?.InvoiceId : null;
    
    if (!currentInvoiceNumber && !currentInvoiceId) {
      alert(t('endOfDayReport.saleDetail.errors.invoiceNumberMissing'));
      return;
    }

    if (!companyId) {
      alert(t('endOfDayReport.saleDetail.errors.companyMissing'));
      return;
    }

    setLoadingInvoice(true);
    try {
      let invoice: any = null;
      
      // InvoiceId varsa doğrudan getir (kalemler dahil)
      if (currentInvoiceId) {
        invoice = await invoiceService.getInvoiceById(currentInvoiceId);
      }
      
      // InvoiceId yoksa veya bulunamadıysa, fatura numarasıyla ara
      if (!invoice && currentInvoiceNumber) {
        const allInvoices = await invoiceService.getInvoicesByCompany();
        const found = allInvoices.find(inv => inv.invoiceNumber === currentInvoiceNumber);
        if (found?.id) {
          invoice = await invoiceService.getInvoiceById(found.id);
        }
      }

      if (invoice) {
        const invoiceData: InvoiceData = {
          id: invoice.id,
          invoiceNumber: invoice.invoiceNumber || '',
          account: {
            companyName: invoice.accountName || t('endOfDayReport.saleDetail.generalAccount'),
            taxNumber: '',
            taxOffice: '',
            address: '',
            phone: '',
            email: '',
          },
          date: invoice.invoiceDate || new Date().toISOString(),
          dueDate: invoice.dueDate,
          items: invoice.items?.map((item: any) => ({
            itemName: item.name || item.itemName || item.itemName || t('endOfDayReport.saleDetail.productName'),
            quantity: item.quantity || 0,
            unit: item.unit || t('endOfDayReport.saleDetail.piece'),
            unitPrice: item.price || item.unitPrice || 0,
            vatRate: item.vatRate || 0,
            sctRate: item.sctRate || 0,
            discountRate: item.discountRate || 0,
            lineTotal: (item.quantity || 0) * (item.price || item.unitPrice || 0),
            vatAmount: ((item.quantity || 0) * (item.price || item.unitPrice || 0) * (item.vatRate || 0)) / 100,
            grossTotal: item.total || item.grossTotal || 0,
          })) || [],
          notes: invoice.notes || '',
          subtotal: invoice.subtotal || 0,
          vatTotal: invoice.vatTotal || 0,
          sctTotal: invoice.sctTotal || 0,
          discountTotal: invoice.discountTotal || 0,
          grandTotal: invoice.grandTotal || 0,
          currency: invoice.currency || 'TRY',
          type: 'quick_sale',
          isClosed: true,
          description: invoice.notes || '',
          createdAt: invoice.createdAt,
        };
        
        setSelectedInvoice(invoiceData);
        setInvoiceModalOpen(true);
      } else {
        alert(t('endOfDayReport.saleDetail.errors.invoiceNotFound', { invoiceNumber: currentInvoiceNumber || '-' }));
      }
    } catch (err: any) {
      console.error('Fatura yükleme hatası:', err);
      alert(t('endOfDayReport.saleDetail.errors.invoiceLoadFailed', { message: err.message }));
    } finally {
      setLoadingInvoice(false);
    }
  };

  // Modal kapatma
  const handleClose = () => {
    setSaleItems([]);
    setInvoiceItems([]);
    setAccountName(null);
    onClose();
  };

  // ✅ Hem restoran hem hızlı satış için kontrol
  if (!saleDetail && !quickSaleDetail) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: isQuickSale ? 'secondary.main' : 'primary.main',
          color: 'white',
          py: 1.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isQuickSale ? <PointOfSaleIcon /> : <ReceiptLongIcon />}
            <Typography variant="h6">
              {isQuickSale ? t('endOfDayReport.saleDetail.quickSaleTitle') : t('endOfDayReport.saleDetail.saleTitle')}
            </Typography>
          </Box>
          {invoiceNumber && (
            <Chip 
              label={t('endOfDayReport.saleDetail.invoiceChip', { invoiceNumber })}
              size="small"
              sx={{ bgcolor: 'white', color: isQuickSale ? 'secondary.main' : 'primary.main', fontWeight: 'bold' }}
            />
          )}
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 0 }}>
          {/* ✅ HIZLI SATIŞ İÇİN ÖZET */}
          {isQuickSale && quickSaleDetail && (
            <>
              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.receiptNo')}</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {quickSaleDetail.ReceiptNumber || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.invoiceNo')}</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {quickSaleDetail.InvoiceNumber || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.date')}</Typography>
                    <Typography variant="body2">
                      {formatDateTime(quickSaleDetail.CreatedAt, false)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.paymentType')}</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {quickSaleDetail.PaymentType === 'CASH' ? t('endOfDayReport.paymentTypes.cash') : 
                       quickSaleDetail.PaymentType === 'POS' ? 'POS' : 
                       quickSaleDetail.PaymentType === 'PREPAID' ? t('endOfDayReport.paymentTypes.prepaid') :
                       quickSaleDetail.PaymentType === 'PARTIAL' ? t('endOfDayReport.paymentTypes.mixed') : quickSaleDetail.PaymentType}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.account')}</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {accountName || quickSaleDetail.AccountName || t('endOfDayReport.saleDetail.generalAccount')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.processedBy')}</Typography>
                    <Typography variant="body2" fontWeight="medium" color="primary.main">
                      {quickSaleDetail.CashierName || quickSaleDetail.CreatedByName || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.columns.status')}</Typography>
                    <Chip 
                      label={quickSaleDetail.PaymentStatus === 'PAID' ? t('endOfDayReport.status.paid') : 
                             quickSaleDetail.PaymentStatus === 'CANCELLED' ? t('endOfDayReport.status.cancelled') : quickSaleDetail.PaymentStatus}
                      size="small"
                      color={quickSaleDetail.PaymentStatus === 'PAID' ? 'success' : 
                             quickSaleDetail.PaymentStatus === 'CANCELLED' ? 'error' : 'default'}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Ödeme Detayları */}
              {quickSaleDetail.Payments && quickSaleDetail.Payments.length > 0 && (
                <>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptLongIcon fontSize="small" color="secondary" />
                      {t('endOfDayReport.saleDetail.paymentDetails', { count: quickSaleDetail.Payments.length })}
                    </Typography>
                    
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.paymentType')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.cashBank')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.columns.amount')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {quickSaleDetail.Payments.map((payment: any, index: number) => (
                            <TableRow key={index} hover>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={payment.PaymentMethod === 'cash' ? t('endOfDayReport.paymentTypes.cash') : 
                                         payment.PaymentMethod === 'pos' ? 'POS' : 
                                         payment.PaymentMethod === 'prepaid' ? t('endOfDayReport.paymentTypes.prepaid') : payment.PaymentMethod}
                                  size="small"
                                  color={payment.PaymentMethod === 'cash' ? 'success' : payment.PaymentMethod === 'prepaid' ? 'info' : 'primary'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                {payment.CashboxName || 
                                 (payment.BankSubAccountName 
                                   ? `${payment.BankAccountName} - ${payment.BankSubAccountName}` 
                                   : payment.BankAccountName) || '-'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(payment.Amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  <Divider />
                </>
              )}

              {/* Fatura Kalemleri */}
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PointOfSaleIcon fontSize="small" color="secondary" />
                  {t('endOfDayReport.saleDetail.invoiceItems', { count: invoiceItems.length })}
                </Typography>
                
                {loadingInvoiceItems ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.productName')}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.quantity')}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.unitPrice')}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.total')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoiceItems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                              <Typography color="text.secondary">
                                {t('endOfDayReport.saleDetail.invoiceItemsNotFound')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          invoiceItems.map((item: any, index: number) => {
                            const itemName = item.itemName || item.name || item.Name || t('endOfDayReport.saleDetail.productName');
                            const quantity = item.quantity || item.Quantity || 1;
                            const unitPrice = item.unitPrice || item.price || item.Price || 0;
                            // ✅ DÜZELTME: KDV hesaplaması YAPILMAYACAK - sadece Miktar * Birim Fiyat
                            const lineTotal = item.total || item.lineTotal || item.grossTotal || (quantity * unitPrice);
                            
                            return (
                              <TableRow key={index} hover>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {itemName}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{quantity}</TableCell>
                                <TableCell align="right">{formatCurrency(unitPrice)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  {formatCurrency(lineTotal)}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              <Divider />

              {/* Toplamlar */}
              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{t('endOfDayReport.columns.subtotal')}:</Typography>
                        {/* İndirim varsa "indirimsiz toplam" (KDV dahil) göster → Ara Toplam − İndirim = Genel Toplam tutar */}
                        <Typography variant="body2">{formatCurrency(quickSaleDetail.DiscountAmount > 0 ? (quickSaleDetail.TotalAmount + quickSaleDetail.DiscountAmount) : quickSaleDetail.SubTotal)}</Typography>
                      </Box>
                      {quickSaleDetail.DiscountAmount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" color="error">{t('endOfDayReport.saleDetail.discount')}:</Typography>
                          <Typography variant="body2" color="error">-{formatCurrency(quickSaleDetail.DiscountAmount)}</Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" fontWeight="bold">{t('endOfDayReport.columns.grandTotal')}:</Typography>
                        <Typography variant="subtitle1" fontWeight="bold" color="secondary.main">
                          {formatCurrency(quickSaleDetail.TotalAmount)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}

          {/* ✅ RESTORAN İÇİN ÖZET (eski kod) */}
          {!isQuickSale && saleDetail && (
            <>
              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.table')}</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {saleDetail.TableName || t('endOfDayReport.activeTables.tablePrefix') + ' ' + saleDetail.TableId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.saleNo')}</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {saleDetail.SaleNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.columns.openedAt')}</Typography>
                    <Typography variant="body2">
                      {formatDateTime(saleDetail.OpenedAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.columns.closedAt')}</Typography>
                    <Typography variant="body2">
                      {formatDateTime(saleDetail.CompletedAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.paymentType')}</Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      color={saleDetail.PaymentMethod?.toLowerCase() === 'unpaid' ? 'error.main' : 'text.primary'}
                    >
                      {saleDetail.PaymentMethod?.toLowerCase() === 'unpaid' 
                        ? t('endOfDayReport.saleDetail.receiptCancelled') 
                        : saleDetail.PaymentMethod}
                    </Typography>
                  </Grid>
                  {saleDetail.PaymentMethod?.toLowerCase() === 'unpaid' && saleDetail.CancellationReason && (
                    <Grid item xs={12}>
                      <Box sx={{ 
                        bgcolor: 'error.light', 
                        color: 'error.contrastText', 
                        px: 2, 
                        py: 1, 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Typography variant="body2" fontWeight="bold">
                          {t('endOfDayReport.saleDetail.cancelReason')}:
                        </Typography>
                        <Typography variant="body2">
                          {saleDetail.CancellationReason}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.columns.seatingDuration')}</Typography>
                    <Typography variant="body2">
                      {formatDuration(
                        saleDetail.DurationMinutes || 0,
                        t('endOfDayReport.saleDetail.durationHourShort'),
                        t('endOfDayReport.saleDetail.durationMinuteShort')
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.openedBy')}</Typography>
                    <Typography variant="body2">
                      {saleDetail.OpenedByName || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.closedBy')}</Typography>
                    <Typography variant="body2" fontWeight="medium" color="primary.main">
                      {saleDetail.ClosedByWaiterName || saleDetail.CompletedByName || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('endOfDayReport.saleDetail.account')}</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {loadingAccount ? (
                        <CircularProgress size={14} />
                      ) : (
                        accountName || t('endOfDayReport.saleDetail.generalAccount')
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Ödeme Detayları (Payments dizisi varsa) */}
              {saleDetail.Payments && saleDetail.Payments.length > 0 && (
                <>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptLongIcon fontSize="small" color="primary" />
                      {t('endOfDayReport.saleDetail.paymentDetails', { count: saleDetail.Payments.length })}
                    </Typography>
                    
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.paymentType')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.cashBank')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.columns.amount')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {saleDetail.Payments.map((payment, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={
                                    payment.PaymentMethod === 'cash' ? t('endOfDayReport.paymentTypes.cash') : 
                                    payment.PaymentMethod === 'pos' ? 'POS' : 
                                    payment.PaymentMethod === 'prepaid' ? t('endOfDayReport.paymentTypes.prepaid') :
                                    payment.PaymentMethod === 'open_account' ? t('endOfDayReport.paymentTypes.openAccount') :
                                    payment.PaymentMethod
                                  }
                                  size="small"
                                  color={
                                    payment.PaymentMethod === 'cash' ? 'success' : 
                                    payment.PaymentMethod === 'pos' ? 'primary' : 
                                    'default'
                                  }
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                {payment.CashboxName || 
                                 (payment.BankSubAccountName 
                                   ? `${payment.BankAccountName} - ${payment.BankSubAccountName}` 
                                   : payment.BankAccountName) || '-'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(payment.Amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  <Divider />
                </>
              )}

              {/* Ürünler Tablosu */}
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RestaurantIcon fontSize="small" color="primary" />
                  {t('endOfDayReport.saleDetail.orderItems', { count: saleItems.length })}
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.productName')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.quantity')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.unitPrice')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.total')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.addedBy')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('endOfDayReport.saleDetail.time')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {saleItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                            <Typography color="text.secondary">
                              {t('endOfDayReport.saleDetail.productInfoNotFound')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        saleItems.map((item: any, index: number) => {
                          // ✅ camelCase formatına göre değerleri al (backend JsonNamingPolicy.CamelCase kullanıyor)
                          const productName = item.productName || item.ProductName || item.name || item.Name || t('endOfDayReport.saleDetail.productName');
                          const quantity = item.quantity || item.Quantity || 1;
                          const unitPrice = item.unitPrice || item.UnitPrice || item.price || item.Price || 0;
                          const totalPrice = item.totalPrice || item.TotalPrice || (quantity * unitPrice);
                          const notes = item.notes || item.Notes;
                          const addedBy = item.addedBy || item.AddedBy || '-';
                          const addedAt = item.addedAt || item.AddedAt;
                          
                          return (
                            <TableRow key={index} hover>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {productName}
                                </Typography>
                                {notes && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {t('endOfDayReport.saleDetail.note')}: {notes}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {quantity}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(unitPrice)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(totalPrice)}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {addedBy}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {addedAt ? formatDateTime(addedAt) : '-'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Divider />

              {/* Toplamlar */}
              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{t('endOfDayReport.columns.subtotal')}:</Typography>
                        <Typography variant="body2">{formatCurrency(saleDetail.SubTotal)}</Typography>
                      </Box>
                      {saleDetail.DiscountAmount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" color="error">{t('endOfDayReport.saleDetail.discount')}:</Typography>
                          <Typography variant="body2" color="error">-{formatCurrency(saleDetail.DiscountAmount)}</Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" fontWeight="bold">{t('endOfDayReport.columns.grandTotal')}:</Typography>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                          {formatCurrency(saleDetail.TotalAmount)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Button 
            onClick={handleClose}
            variant="outlined"
          >
            {t('endOfDayReport.common.cancel')}
          </Button>
          <Tooltip 
            title={
              !isQuickSale && saleDetail?.PaymentMethod?.toLowerCase() === 'unpaid'
                ? t('endOfDayReport.saleDetail.tooltips.cancelledNoInvoice')
                : !invoiceNumber
                  ? t('endOfDayReport.saleDetail.tooltips.noInvoice')
                  : ''
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                color={isQuickSale ? 'secondary' : 'primary'}
                onClick={handleViewInvoice}
                disabled={!invoiceNumber || loadingInvoice}
                startIcon={loadingInvoice ? <CircularProgress size={16} color="inherit" /> : <ReceiptLongIcon />}
              >
                {loadingInvoice ? t('endOfDayReport.common.loading') : t('endOfDayReport.saleDetail.viewInvoice')}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Fatura Görüntüleme Modalı */}
      <InvoiceDisplayModal
        open={invoiceModalOpen}
        onClose={() => {
          setInvoiceModalOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        companyId={companyId}
      />
    </>
  );
};

export default SaleDetailModal;
